import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import { useAuth } from '../../shared/contexts/AuthContext';
import { hasPermission } from '../../shared/utils/permissions';
import { isPathAllowedByPageAccess, isLedgerSubPathAllowed } from '../../shared/utils/pageAccessMatch';

interface PageAccessRouteProps {
  children: React.ReactNode;
  requiredPath?: string;
}

// Payment-related pages that require payments.read permission
const PAYMENT_PAGES = [
  '/app/payments',
  '/app/payouts',
  '/app/expenses',
  '/app/outflow',
  '/app/ledger',
  '/app/collections',
  '/app/partner-collections',
  '/app/ledger/interac-payment-claims',
  '/app/ledger/bank-statements',
  '/app/ledger/reconciliation',
  '/app/ledger/credit-card-statements',
  '/app/ledger/credit-card-reconciliation',
];

const PageAccessRoute: React.FC<PageAccessRouteProps> = ({ 
  children, 
  requiredPath 
}) => {
  const { user } = useAuth();
  const location = useLocation();

  // If no user is logged in, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const currentPath = requiredPath || location.pathname;
  const userPermissions = user?.permissions || [];

  // Ledger / revenue / outflow: allow payments.read OR explicit page access (Manage Users)
  const isPaymentPage = PAYMENT_PAGES.some((page) => currentPath.startsWith(page));
  if (isPaymentPage) {
    const pa = user.pageAccess || [];
    const allowedByPageAccess = isPathAllowedByPageAccess(pa, currentPath);
    if (!hasPermission(userPermissions, 'payments.read') && !allowedByPageAccess) {
      return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Alert severity="warning" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Access Denied
            </Typography>
            <Typography>
              You don't have permission to access payment-related pages. Please contact your administrator if you believe this is an error.
            </Typography>
          </Alert>
        </Box>
      );
    }
  }

  // Home is always reachable (default landing) regardless of pageAccess
  if (currentPath === '/app' || currentPath === '/app/') {
    return <>{children}</>;
  }

  // Profile is always reachable for signed-in users (not part of pageAccess toggles)
  if (currentPath === '/app/profile' || currentPath.startsWith('/app/profile/')) {
    return <>{children}</>;
  }

  // Normalize role for robust checking
  const role = user.permissionRole?.toLowerCase().replace(/\s+/g, '_');

  // Ledger sub-page access control — applies to ALL roles equally (admin, ops_manager, members)
  if (currentPath.startsWith('/app/ledger/') && currentPath !== '/app/ledger/') {
    // 1. Page Access management page gate: Admin or explicitly delegated managers
    if (currentPath.startsWith('/app/ledger/page-access')) {
      const pa = user.pageAccess || [];
      const isManagerAuthorized =
        role === 'admin' ||
        role === 'operations_manager' ||
        isPathAllowedByPageAccess(pa, '/app/ledger/page-access');
      const isSubPageAllowed = isLedgerSubPathAllowed(user.ledgerPageAccess, currentPath, user.permissionRole);

      if (!isManagerAuthorized || !isSubPageAllowed) {
        return (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Alert severity="warning" sx={{ maxWidth: 600 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Access Denied
              </Typography>
              <Typography>
                You don't have permission to access Ledger Page Access. Please contact your administrator if you believe this is an error.
              </Typography>
            </Alert>
          </Box>
        );
      }
      return <>{children}</>;
    }

    // 2. Granular sub-page restriction — blocks users from ungranted subledgers
    if (!isLedgerSubPathAllowed(user.ledgerPageAccess, currentPath, user.permissionRole)) {
      return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Alert severity="warning" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Access Denied
            </Typography>
            <Typography>
              You don't have permission to access this ledger sub-page. Please contact your administrator if you believe this is an error.
            </Typography>
          </Alert>
        </Box>
      );
    }
  }

  // Operations manager: all pages (except payment gate above and ledger sub-page gate above)
  if (role === 'operations_manager') {
    return <>{children}</>;
  }

  // Admin: same pageAccess rules as everyone else (no "empty list = full access" bypass),
  // EXCEPT for critical new modules which are always visible to admins.
  if (role === 'admin') {
    if (
      currentPath.startsWith('/app/legal-tasks') ||
      currentPath.startsWith('/app/my-tasks') ||
      currentPath.startsWith('/app/my-attendance') ||
      currentPath.startsWith('/app/my-training') ||
      currentPath.startsWith('/app/my-policies') ||
      currentPath.startsWith('/app/my-compliance') ||
      currentPath.startsWith('/app/sheets')
    ) {
      return <>{children}</>;
    }
    const pa = user.pageAccess || [];
    if (isPathAllowedByPageAccess(pa, currentPath)) {
      return <>{children}</>;
    }
  } else if (user.pageAccess && user.pageAccess.length > 0) {
    if (isPathAllowedByPageAccess(user.pageAccess, currentPath)) {
      return <>{children}</>;
    }
  }

  // User doesn't have access to this page
  return (
    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
      <Alert severity="warning" sx={{ maxWidth: 600 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Access Denied
        </Typography>
        <Typography>
          You don't have permission to access this page. Please contact your administrator if you believe this is an error.
        </Typography>
      </Alert>
    </Box>
  );
};

export default PageAccessRoute; 